// The app's state and every change to it. State is immutable: each action builds the next
// state, shows it at once and saves it. Grading waits for the save, so an answer is never
// lost between cards.

import { useSyncExternalStore } from 'react';
import { course, getItem, reviewCourse } from '../content';
import type { Course, Goal } from '../content/types';
import { dayKey, endOfDay } from '../lib/day';
import { hasWork, newSession, planDay } from '../lib/plan';
import { cardId, learningStep, newCard, review, type CardRecord, type Grade } from '../lib/scheduler';
import { freshState, type Attempt, type FlagReason, type Persisted, type SessionState, type Settings, type StoryProgress } from './model';
import * as storage from './storage';

let state: Persisted | null = null;
let persistent = true;
/** Saved progress exists but couldn't be read: never overwrite it with a fresh start. */
let readOnly = false;
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getState(): Persisted {
  if (!state) throw new Error('Store not ready');
  return state;
}

/** Other tabs, or the installed app, keep the same progress: each says when it has saved. */
const channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('learning-greek');
(channel as { unref?: () => void } | null)?.unref?.();
let saving = 0;

function commit(next: Persisted): Promise<void> {
  state = next;
  emit();
  if (readOnly) return Promise.resolve();
  saving++;
  return storage
    .saveState(next)
    .then(() => channel?.postMessage('saved'))
    .catch(() => {
      persistent = false;
      emit();
    })
    .finally(() => saving--);
}

/**
 * Takes up what another tab saved (or what was saved while this one sat in the background), so
 * that a tab left open never writes its older progress over newer.
 */
async function reload(now = new Date()) {
  if (readOnly || saving || !state) return;
  try {
    const saved = await storage.loadState();
    if (!saved || saving) return;
    state = reconcile(saved, reviewCourse, now);
    emit();
  } catch {
    // Keep what this tab has.
  }
}

let watching = false;
function watchOtherTabs() {
  if (watching) return;
  watching = true;
  channel?.addEventListener('message', () => void reload());
  if (typeof document !== 'undefined')
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void reload();
    });
}

/** Keeps cards in step with the installed content: retired phrases leave scheduling, changed ones restart. */
export function reconcile(saved: Persisted, content: Course, now: Date): Persisted {
  const items = new Map(content.items.map((i) => [i.id, i]));
  const cards: Record<string, CardRecord> = {};
  for (const [id, card] of Object.entries(saved.cards)) {
    const item = items.get(card.itemId);
    if (!item) continue;
    // A changed phrase starts again, but keeps the day it was met (so it doesn't use up today's
    // new phrases) and, for a phrase you say, its hear-it card waits a day as when first met.
    cards[id] =
      item.text === card.text ? card : newCard(card.itemId, card.kind, item.text, card.introduced ?? dayKey(now), now, card.kind === 'hear' && item.role === 'say' ? endOfDay(now) : undefined);
  }
  const lessons = new Set(content.lessons.map((l) => l.id));
  const sessionFits =
    saved.session?.steps.every((st) => {
      if ('cardId' in st) return items.has(st.cardId.split(':')[0]);
      if ('itemId' in st) return items.has(st.itemId);
      if ('itemIds' in st) return st.itemIds.every((id) => items.has(id));
      return lessons.has(st.lessonId);
    }) ?? true;
  return {
    ...saved,
    // A session planned for different content is dropped; the next start plans afresh.
    session: sessionFits ? saved.session : null,
    cards,
    settings: { ...freshState(now).settings, ...saved.settings },
    profile: { ...freshState(now).profile, ...saved.profile, lessonsDone: saved.profile.lessonsDone.filter((l) => lessons.has(l)) },
  };
}

export async function initStore(now = new Date()): Promise<void> {
  let saved: Persisted | null = null;
  readOnly = false;
  try {
    saved = await storage.loadState();
  } catch {
    // Keep whatever is stored; this visit runs in memory only.
    saved = null;
    readOnly = true;
  }
  persistent = !readOnly && (await storage.isPersistent());
  state = saved ? reconcile(saved, reviewCourse, now) : freshState(now);
  emit();
  storage.requestPersistence();
  watchOtherTabs();
}

/** After a screen failed: today's unfinished session is set aside (saved unless progress couldn't be read). */
export function setAsideSession() {
  return update((s) => ({ ...s, session: null }));
}

/** Replaces the state directly (tests, and recovery after an error). */
export function setStateForTest(next: Persisted) {
  state = next;
  emit();
}

export function useAppState(): Persisted {
  return useSyncExternalStore(subscribe, getState, getState);
}

export function usePersistent(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => persistent,
    () => persistent,
  );
}

function update(fn: (s: Persisted) => Persisted): Promise<void> {
  return commit(fn(getState()));
}

// ── Profile and settings ─────────────────────────────────────────────

export function completeOnboarding(habit: string | null) {
  return update((s) => ({ ...s, profile: { ...s.profile, onboarded: true, habit } }));
}

/** The learner's goal. Switching keeps everything learned: the tracks share their first units. */
export function setGoal(goal: Goal) {
  return update((s) => ({ ...s, profile: { ...s.profile, goal } }));
}

export function setSettings(patch: Partial<Settings>) {
  return update((s) => ({ ...s, settings: { ...s.settings, ...patch } }));
}

export function setHabit(habit: string | null) {
  return update((s) => ({ ...s, profile: { ...s.profile, habit } }));
}

export function markMicExplained() {
  return update((s) => ({ ...s, profile: { ...s.profile, micExplained: true } }));
}

export function markReadingGuideSeen() {
  if (getState().profile.readingGuideSeen) return Promise.resolve();
  return update((s) => ({ ...s, profile: { ...s.profile, readingGuideSeen: true } }));
}

/** "Can't speak now": today's session leaves the say-it cards due and shows the rest. */
export function setQuiet(on: boolean, now = new Date()) {
  const day = dayKey(now);
  return update((s) => ({
    ...s,
    profile: { ...s.profile, quietDay: on ? day : null },
    // A session planned the other way no longer fits; the next start plans afresh.
    session: s.session && !s.session.completedAt && s.session.quiet !== on ? null : s.session,
  }));
}

// ── Sessions ─────────────────────────────────────────────────────────

/** The session to show today: resumes an unfinished one, or plans a new one. Null when nothing is due. */
export async function startSession(now = new Date(), opts: { reviewsOnly?: boolean } = {}): Promise<SessionState | null> {
  const s = getState();
  const day = dayKey(now);
  const quiet = s.profile.quietDay === day;
  const reviewsOnly = Boolean(opts.reviewsOnly);
  // Today's unfinished session carries on; one not yet begun is planned again if asked for the other kind.
  const open = s.session && s.session.day === day && !s.session.completedAt && s.session.quiet === quiet ? s.session : null;
  if (open && (open.index > 0 || Boolean(open.reviewsOnly) === reviewsOnly)) return open;
  const full = planDay(s, reviewCourse, now);
  // "Just the reviews": today's due cards only; the new phrases and the role-play wait.
  const plan = reviewsOnly ? { ...full, newItems: [], sceneLessons: [] } : full;
  if (!hasWork(plan)) return null;
  const session = { ...newSession(plan, s, course, now), ...(reviewsOnly ? { reviewsOnly } : {}) };
  await commit({ ...s, session });
  return session;
}

/** Takes one more lesson today, by the learner's choice. Planned and saved in one step. */
export async function learnEarly(now = new Date()): Promise<SessionState | null> {
  const s = getState();
  const day = dayKey(now);
  const current = s.profile.extraNew?.day === day ? s.profile.extraNew.count : 0;
  const next: Persisted = { ...s, profile: { ...s.profile, extraNew: { day, count: current + s.settings.newPerDay } } };
  const plan = planDay(next, reviewCourse, now);
  if (!hasWork(plan)) {
    await commit(next);
    return null;
  }
  const session = newSession(plan, next, course, now);
  await commit({ ...next, session });
  return session;
}

function withSession(s: Persisted, fn: (session: SessionState) => SessionState): Persisted {
  if (!s.session) return s;
  return { ...s, session: fn(s.session) };
}

function creditDay(now: Date) {
  const s = getState();
  const day = dayKey(now);
  if (s.session?.completedAt && !s.profile.practiceDays.includes(day)) {
    return update((x) => ({ ...x, profile: { ...x.profile, practiceDays: [...x.profile.practiceDays, day] } }));
  }
}

/**
 * Moves to the next step; finishing the last step completes the session and counts the day.
 * `from` is the step the caller is on: a second tap on the same step does nothing.
 */
export function advance(now = new Date(), from?: number) {
  const s = getState();
  if (from !== undefined && s.session?.index !== from) return Promise.resolve();
  return update((x) =>
    withSession(x, (session) => {
      const index = session.index + 1;
      if (index < session.steps.length) return { ...session, index };
      return { ...session, index, completedAt: now.toISOString() };
    }),
  ).then(() => creditDay(now));
}

/** A step whose audio would not play: it leaves today's session, and its card stays due. */
export function skipStep(now = new Date(), from?: number) {
  const s = getState();
  if (from !== undefined && s.session?.index !== from) return Promise.resolve();
  return update((x) => withSession(x, (session) => ({ ...session, skipped: session.skipped + 1 }))).then(() => advance(now, from));
}

/**
 * Meeting a phrase creates its cards. A phrase you say gets its hear-it card only from the day
 * after its first real review, so the two cards of a phrase don't give each other away.
 */
export function introduce(itemId: string, now = new Date()) {
  const item = getItem(itemId);
  const day = dayKey(now);
  return update((s) => {
    const cards = { ...s.cards };
    // Met by ear on a listening-only day, the phrase is checked by its hear-it card today; the
    // say-it card waits, due, for the next day the learner can speak.
    const quiet = Boolean(s.session?.quiet);
    if (item.role === 'say') {
      if (!cards[cardId(itemId, 'say')]) cards[cardId(itemId, 'say')] = newCard(itemId, 'say', item.text, day, now);
      if (!cards[cardId(itemId, 'hear')])
        cards[cardId(itemId, 'hear')] = newCard(itemId, 'hear', item.text, day, now, quiet ? undefined : endOfDay(endOfDay(now)));
    } else if (!cards[cardId(itemId, 'hear')]) {
      cards[cardId(itemId, 'hear')] = newCard(itemId, 'hear', item.text, day, now);
    }
    const next = { ...s, cards };
    return withSession(next, (session) => (session.met.includes(itemId) ? session : { ...session, met: [...session.met, itemId] }));
  });
}

/** Counts a showing of a card, which picks the voice for the next one. */
export function presented(id: string) {
  const card = getState().cards[id];
  if (!card) return Promise.resolve();
  return update((s) => ({ ...s, cards: { ...s.cards, [id]: { ...card, presentations: card.presentations + 1 } } }));
}

export interface GradeDetails {
  repeat?: boolean;
  /** The check right after meeting the phrase: logged, and the card comes back tomorrow. */
  learning?: boolean;
  hint?: boolean;
  recorded?: boolean;
  pick?: string;
  /** The rough match with the model, 0–100, when the learner recorded themselves. */
  match?: number | null;
}

/**
 * Records an answer. A review is scheduled with FSRS; the check right after meeting a phrase is a
 * learning step (back tomorrow); a repeat later in the session is only logged. A missed card comes
 * back once, before the lesson's scene if there is one, otherwise at the end.
 */
export async function grade(id: string, value: Grade, details: GradeDetails = {}, now = new Date()) {
  const s = getState();
  const card = s.cards[id];
  if (!card) return;
  const day = dayKey(now);
  const attempt: Attempt = {
    cardId: id,
    at: now.toISOString(),
    grade: value,
    pick: details.pick,
    repeat: Boolean(details.repeat),
    learning: Boolean(details.learning),
    hint: Boolean(details.hint),
    recorded: Boolean(details.recorded),
    ...(details.match !== undefined ? { match: details.match } : {}),
    contentVersion: course.contentVersion,
    sessionId: s.session?.id ?? 'none',
  };
  try {
    await storage.addAttempt(attempt);
  } catch {
    persistent = false;
  }
  const failed = value === 'missed' || value === 'wrong';
  await update((x) => {
    const current = x.cards[id] ?? card;
    const updated = details.repeat ? current : details.learning ? learningStep(current, now) : review(current, value, now);
    const cards = { ...x.cards, [id]: updated };
    const reviewsByDay = details.repeat ? x.profile.reviewsByDay : { ...x.profile.reviewsByDay, [day]: (x.profile.reviewsByDay[day] ?? 0) + 1 };
    const next: Persisted = { ...x, cards, profile: { ...x.profile, reviewsByDay } };
    return withSession(next, (session) => {
      const alreadyQueued = session.steps.some((st, i) => i > session.index && st.kind === 'repeat' && st.cardId === id);
      let steps = session.steps;
      if (failed && !details.repeat && !alreadyQueued) {
        const repeat = { kind: 'repeat' as const, cardId: id };
        const scene = steps.findIndex((st, i) => i > session.index && st.kind === 'scene');
        steps = scene >= 0 ? [...steps.slice(0, scene), repeat, ...steps.slice(scene)] : [...steps, repeat];
      }
      const onStep = session.steps[session.index];
      const answered = onStep && 'cardId' in onStep && onStep.cardId === id ? [...(session.answered ?? []), session.index] : session.answered;
      return { ...session, steps, graded: { ...session.graded, [id]: value }, answered };
    });
  });
}

/** Skipping a scene leaves the lesson open, so the scene is offered again next time. */
export function skipScene(lessonId: string, now = new Date()) {
  const current = getState().session;
  const step = current?.steps[current.index];
  if (!step || step.kind !== 'scene' || step.lessonId !== lessonId) return Promise.resolve();
  return update((s) =>
    withSession(s, (session) => {
      const after = session.steps[session.index + 1];
      const skip = after?.kind === 'lesson-done' && after.lessonId === lessonId ? 2 : 1;
      const index = session.index + skip;
      return index < session.steps.length ? { ...session, index } : { ...session, index, completedAt: now.toISOString() };
    }),
  ).then(() => creditDay(now));
}

export function finishLesson(lessonId: string, now = new Date()) {
  return update((s) =>
    s.profile.lessonsDone.includes(lessonId)
      ? s
      : {
          ...s,
          profile: {
            ...s.profile,
            lessonsDone: [...s.profile.lessonsDone, lessonId],
            missions: { ...s.profile.missions, [lessonId]: { day: dayKey(now) } },
          },
        },
  );
}

/** The answer to "Did you try it for real?", asked once, on a later day. */
export function answerMission(lessonId: string, tried: boolean) {
  return update((s) => {
    const mission = s.profile.missions?.[lessonId];
    if (!mission) return s;
    return { ...s, profile: { ...s.profile, missions: { ...s.profile.missions, [lessonId]: { ...mission, tried } } } };
  });
}

// ── Flags ────────────────────────────────────────────────────────────

/** Flagging a phrase takes its cards out of practice until the flag is withdrawn. */
export function flag(itemId: string, reason: FlagReason, note?: string, now = new Date()) {
  return update((s) => ({
    ...s,
    profile: { ...s.profile, flags: { ...s.profile.flags, [itemId]: { reason, note: note?.trim() || undefined, at: now.toISOString() } } },
    session: s.session
      ? {
          ...s.session,
          steps: s.session.steps.filter((st, i) => i <= s.session!.index || !stepTouches(st, itemId)),
        }
      : null,
  }));
}

function stepTouches(step: SessionState['steps'][number], itemId: string): boolean {
  if ('cardId' in step) return step.cardId.startsWith(`${itemId}:`);
  if ('itemId' in step) return step.itemId === itemId;
  // A listening contrast survives losing one phrase as long as two remain (checked when shown).
  return false;
}

export function unflag(itemId: string) {
  return update((s) => {
    const flags = { ...s.profile.flags };
    delete flags[itemId];
    return { ...s, profile: { ...s.profile, flags } };
  });
}

// ── Data ─────────────────────────────────────────────────────────────

export async function exportData(): Promise<string> {
  const attempts = await storage.allAttempts();
  return JSON.stringify(
    { app: 'learning-greek-web', exportedAt: new Date().toISOString(), contentVersion: course.contentVersion, state: getState(), attempts },
    null,
    2,
  );
}

export async function resetProgress(now = new Date()) {
  await storage.clearAll();
  const fresh = freshState(now);
  const s = getState();
  // Keep the learner's choices; clear what they learned.
  await commit({ ...fresh, settings: s.settings, profile: { ...fresh.profile, onboarded: true, goal: s.profile.goal, habit: s.profile.habit } });
}

// ── Stories ──────────────────────────────────────────────────────────

function storyOf(s: Persisted, id: string, now: Date): StoryProgress {
  return s.profile.stories?.[id] ?? { started: now.toISOString(), watches: 0 };
}

/** The learner opened an episode. */
export function storyStarted(id: string, now = new Date()) {
  return update((s) => ({ ...s, profile: { ...s.profile, stories: { ...s.profile.stories, [id]: storyOf(s, id, now) } } }));
}

/** The learner watched an episode to the end. */
export function storyFinished(id: string, now = new Date()) {
  return update((s) => {
    const p = storyOf(s, id, now);
    return { ...s, profile: { ...s.profile, stories: { ...s.profile.stories, [id]: { ...p, finished: p.finished ?? now.toISOString(), watches: p.watches + 1 } } } };
  });
}

/**
 * Phrases kept from an episode become hear-it cards. The episode's own check was their first
 * meeting, so they are first due tomorrow, like a phrase met in a lesson. They don't count against
 * the day's new phrases.
 */
export function keepStoryPhrases(id: string, itemIds: string[], now = new Date()) {
  return update((s) => {
    const day = dayKey(now);
    const cards = { ...s.cards };
    for (const itemId of itemIds) {
      const key = cardId(itemId, 'hear');
      if (cards[key]) continue;
      cards[key] = newCard(itemId, 'hear', getItem(itemId).text, day, now, endOfDay(now));
    }
    const p = storyOf(s, id, now);
    const kept = [...new Set([...(p.kept ?? []), ...itemIds])];
    return { ...s, cards, profile: { ...s.profile, stories: { ...s.profile.stories, [id]: { ...p, kept } } } };
  });
}
